import { Link } from "@tanstack/react-router";
import { CodeTokens } from "#/components/CodeTokens.tsx";
import type { Messages } from "#/i18n/index.ts";
import { localeParam, type Locale } from "#/lib/locale.ts";
import type { PackageStatus } from "#/server/registry.ts";

/**
 * A capability in words: the translated name when the site knows the
 * capability, else the capability as the package declares it.
 *
 * @param messages - Active locale's copy.
 * @param capability - Capability key, for example `importers`.
 * @returns The words to show.
 */
export function capabilityName(messages: Messages, capability: string): string {
  const names = messages.packagesPage.capabilityNames;
  return Object.hasOwn(names, capability) ? names[capability as keyof typeof names] : capability;
}

/**
 * One package as an index card: its name linking to the package page, the
 * version, one sentence, its capabilities in words when given, and the
 * install command (or that it is not released yet).
 *
 * @param props - The package's card fields, active locale and copy.
 * @returns The card, as a list item.
 */
export function PackageCard({
  pkg,
  locale,
  messages,
}: {
  pkg: { name: string; slug: string; description: string; version: string | null; install: string; capabilities?: readonly string[]; status?: PackageStatus; preset?: boolean };
  locale: Locale;
  messages: Messages;
}) {
  return (
    <li className="card package-card">
      <h3 className="package-card__strip" translate="no">
        <Link to="/{-$locale}/packages/$name" params={{ locale: localeParam(locale), name: pkg.slug }}>
          {pkg.name}
        </Link>
        {" · "}{pkg.version ?? "unreleased"}
        {pkg.status === undefined ? null : ` · ${pkg.status}`}
        {pkg.preset === true ? " · preset" : null}
      </h3>
      {pkg.description === "" ? null : <p className="package-card__body" title={pkg.description}>{pkg.description}</p>}
      {pkg.capabilities === undefined || pkg.capabilities.length === 0 ? null : (
        <p className="package-card__capabilities">{pkg.capabilities.map((capability) => capabilityName(messages, capability)).join(" · ")}</p>
      )}
      {pkg.install === "" ? (
        <p className="package-card__install package-card__install--none">{messages.packagesPage.unreleased}</p>
      ) : (
        <code className="package-card__install" translate="no">
          <CodeTokens text={pkg.install} />
        </code>
      )}
    </li>
  );
}
